import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  createGeneralDesignApproval,
  deleteGeneralDesignApproval,
  getGeneralDesignAllContracts,
  getGeneralDesignApproval,
  getGeneralDesignUserReports,
  getGeneralDesignUsersWithReports,
  getRpGpLaborEffortId,
  updateGeneralDesignCorrection,
} from "../../../../api/paymentsProgressApi";
import { getUsersSummary } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import { getApprovalStatusText } from "./paymentsApprovalStatus";
import GeneralDesignTable from "./GeneralDesignTable";

const GROUP_BY_USERS = "users";
const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const parseUserReportsPayload = (data) => {
  const reports = Array.isArray(data)
    ? data
    : Array.isArray(data?.reports)
      ? data.reports
      : [];
  const correction = data && !Array.isArray(data) ? data.correction || {} : {};
  return { reports: Array.isArray(reports) ? reports : [], correction };
};

const uniqueUsersById = (lists) => {
  const map = new Map();
  lists.flat().forEach((user) => {
    const id = Number(user?.id);
    if (!Number.isFinite(id)) return;
    if (!map.has(id)) map.set(id, user);
  });
  return Array.from(map.values());
};

const sortUsersByName = (users) =>
  [...users].sort((a, b) => {
    const left = [a.surname, a.name].filter(Boolean).join(" ");
    const right = [b.surname, b.name].filter(Boolean).join(" ");
    return left.localeCompare(right, "ru");
  });

const formatUserLabel = (user) =>
  [user?.surname, user?.name].filter(Boolean).join(" ").trim() ||
  `ID ${user?.id ?? "—"}`;

const GeneralDesignSection = ({
  isAdms,
  isReadOnly = false,
  grouping = GROUP_BY_USERS,
  month,
  year,
  savingId,
  setSavingId,
  setConfirmModal,
  setCommentModal,
  formatMoney,
}) => {
  const { userData } = useContext(UserContext);
  const isUsersGrouping = grouping === GROUP_BY_USERS;

  const [generalDesignRows, setGeneralDesignRows] = useState([]);
  const [generalDesignUsers, setGeneralDesignUsers] = useState([]);
  const [generalDesignLoading, setGeneralDesignLoading] = useState(false);
  const [generalDesignError, setGeneralDesignError] = useState(null);
  const [expandedGeneralProjects, setExpandedGeneralProjects] = useState(new Set());
  const [expandedGeneralTopUsers, setExpandedGeneralTopUsers] = useState(new Set());
  const [expandedGeneralUsers, setExpandedGeneralUsers] = useState(new Set());
  const [generalUsersByContract, setGeneralUsersByContract] = useState({});
  const [generalReportsByKey, setGeneralReportsByKey] = useState({});
  const [loadingGeneralUsers, setLoadingGeneralUsers] = useState({});
  const [loadingGeneralReports, setLoadingGeneralReports] = useState({});

  const [sendApprovalModalOpen, setSendApprovalModalOpen] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedApprovalUser, setSelectedApprovalUser] = useState(null);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [approvalByUserId, setApprovalByUserId] = useState({});
  const [deletingApproval, setDeletingApproval] = useState(false);
  const [generalDesignExpanded, setGeneralDesignExpanded] = useState(true);
  const [sectionTotalsState, setSectionTotalsState] = useState({});

  const isUserOnApproval = (userId) => Boolean(approvalByUserId[userId]?.id);

  const canDeleteApprovalForUser = (userId) => {
    if (isReadOnly) return false;
    const approval = approvalByUserId[userId];
    if (!approval?.id) return false;
    const creatorId = approval?.creator_id ?? approval?.creator?.id ?? null;
    return creatorId != null && String(creatorId) === String(userData?.id);
  };

  const deleteApprovalForUser = async (user) => {
    if (isReadOnly) return;
    const userId = Number(user?.id);
    const approval = approvalByUserId[userId];
    if (!approval?.id) return;
    const ok = window.confirm(
      `Удалить согласование для сотрудника «${formatUserLabel(user)}»?`,
    );
    if (!ok) return;
    try {
      setDeletingApproval(true);
      await deleteGeneralDesignApproval(userId, Number(year), Number(month));
      setApprovalByUserId((prev) => ({ ...prev, [userId]: null }));
      alert("Согласование удалено");
    } catch (err) {
      alert(err?.message || "Ошибка удаления согласования Генплан РП");
    } finally {
      setDeletingApproval(false);
    }
  };

  const generalUsersByContractRef = useRef(generalUsersByContract);
  const loadingGeneralUsersRef = useRef(loadingGeneralUsers);
  const generalReportsByKeyRef = useRef(generalReportsByKey);
  const loadingGeneralReportsRef = useRef(loadingGeneralReports);

  useEffect(() => {
    generalUsersByContractRef.current = generalUsersByContract;
  }, [generalUsersByContract]);

  useEffect(() => {
    loadingGeneralUsersRef.current = loadingGeneralUsers;
  }, [loadingGeneralUsers]);

  useEffect(() => {
    generalReportsByKeyRef.current = generalReportsByKey;
  }, [generalReportsByKey]);

  useEffect(() => {
    loadingGeneralReportsRef.current = loadingGeneralReports;
  }, [loadingGeneralReports]);

  useEffect(() => {
    setExpandedGeneralProjects(new Set());
    setExpandedGeneralTopUsers(new Set());
    setExpandedGeneralUsers(new Set());
  }, [grouping]);

  const toggleGeneralProject = (contractSectionsId) => {
    setExpandedGeneralProjects((prev) => {
      const next = new Set(prev);
      if (next.has(contractSectionsId)) next.delete(contractSectionsId);
      else next.add(contractSectionsId);
      return next;
    });
  };

  const toggleGeneralTopUser = (userId) => {
    setExpandedGeneralTopUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleGeneralUser = (contractSectionsId, userId) => {
    const key = `${contractSectionsId}-${userId}`;
    setExpandedGeneralUsers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (!isAdms) {
      setGeneralDesignRows([]);
      setGeneralDesignUsers([]);
      setGeneralDesignLoading(false);
      setGeneralDesignError(null);
      return;
    }

    let cancelled = false;
    setGeneralDesignLoading(true);
    setGeneralDesignError(null);
    setExpandedGeneralProjects(new Set());
    setExpandedGeneralTopUsers(new Set());
    setExpandedGeneralUsers(new Set());
    setGeneralUsersByContract({});
    setGeneralReportsByKey({});
    setLoadingGeneralUsers({});
    setLoadingGeneralReports({});
    setApprovalByUserId({});
    setSectionTotalsState({});

    const load = async () => {
      const [allUsersRaw, contractsRaw] = await Promise.all([
        getGeneralDesignUsersWithReports(month, year).catch(() => []),
        getGeneralDesignAllContracts(month, year),
      ]);
      if (cancelled) return;

      const rawRows = (Array.isArray(contractsRaw) ? contractsRaw : []).map((row) => ({
        ...row,
        correction: row?.correction || {},
      }));
      const contractIds = rawRows
        .map((row) => getRpGpLaborEffortId(row))
        .filter((id) => Number.isFinite(id));
      const rowsByContractId = new Map(
        rawRows.map((row) => [getRpGpLaborEffortId(row), row]),
      );

      const filteredRows = [];
      const usersByContract = {};
      const USERS_WITH_REPORTS_CONCURRENCY = 6;
      for (let i = 0; i < contractIds.length; i += USERS_WITH_REPORTS_CONCURRENCY) {
        if (cancelled) return;
        const chunk = contractIds.slice(i, i + USERS_WITH_REPORTS_CONCURRENCY);
        const results = await Promise.all(
          chunk.map(async (contractId) => {
            try {
              const users = await getGeneralDesignUsersWithReports(
                month,
                year,
                contractId,
              );
              return [contractId, Array.isArray(users) ? users : []];
            } catch {
              return [contractId, []];
            }
          }),
        );
        results.forEach(([contractId, users]) => {
          usersByContract[contractId] = users;
          if (users.length > 0) {
            const row = rowsByContractId.get(contractId);
            if (row) filteredRows.push(row);
          }
        });
      }

      if (cancelled) return;
      setGeneralDesignRows(filteredRows);
      setGeneralUsersByContract(usersByContract);

      const usersFromEndpoint = Array.isArray(allUsersRaw) ? allUsersRaw : [];
      const usersFromContracts = uniqueUsersById(Object.values(usersByContract));
      const users =
        usersFromEndpoint.length > 0 ? usersFromEndpoint : usersFromContracts;
      setGeneralDesignUsers(sortUsersByName(users));
    };

    load()
      .catch((err) => {
        if (cancelled) return;
        setGeneralDesignRows([]);
        setGeneralDesignUsers([]);
        setGeneralDesignError(err?.message || "Ошибка загрузки Генплан РП");
      })
      .finally(() => {
        if (!cancelled) setGeneralDesignLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year]);

  useEffect(() => {
    if (!isAdms || expandedGeneralProjects.size === 0) return;
    let cancelled = false;
    const contractIdsToLoad = Array.from(expandedGeneralProjects).filter(
      (contractId) =>
        generalUsersByContractRef.current[contractId] === undefined &&
        !loadingGeneralUsersRef.current[contractId],
    );

    contractIdsToLoad.forEach((contractId) => {
      setLoadingGeneralUsers((prev) => ({ ...prev, [contractId]: true }));
      getGeneralDesignUsersWithReports(month, year, contractId)
        .then((data) => {
          if (cancelled) return;
          setGeneralUsersByContract((prev) => ({
            ...prev,
            [contractId]: Array.isArray(data) ? data : [],
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setGeneralUsersByContract((prev) => ({ ...prev, [contractId]: [] }));
        })
        .finally(() => {
          setLoadingGeneralUsers((prev) => ({ ...prev, [contractId]: false }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year, expandedGeneralProjects]);

  useEffect(() => {
    if (!isAdms || expandedGeneralUsers.size === 0) return;
    let cancelled = false;
    const keysToLoad = Array.from(expandedGeneralUsers).filter(
      (key) =>
        generalReportsByKeyRef.current[key] === undefined &&
        !loadingGeneralReportsRef.current[key],
    );

    keysToLoad.forEach((key) => {
      const [contractSectionsIdRaw, userIdRaw] = key.split("-");
      const contractSectionsId = Number(contractSectionsIdRaw);
      const userId = Number(userIdRaw);
      if (Number.isNaN(contractSectionsId) || Number.isNaN(userId)) return;

      setLoadingGeneralReports((prev) => ({ ...prev, [key]: true }));
      getGeneralDesignUserReports(month, year, contractSectionsId, userId)
        .then((data) => {
          if (cancelled) return;
          setGeneralReportsByKey((prev) => ({
            ...prev,
            [key]: parseUserReportsPayload(data),
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setGeneralReportsByKey((prev) => ({
            ...prev,
            [key]: { correction: {}, reports: [] },
          }));
        })
        .finally(() => {
          setLoadingGeneralReports((prev) => ({ ...prev, [key]: false }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year, expandedGeneralUsers]);

  useEffect(() => {
    if (!isAdms || generalDesignRows.length === 0) return;
    let cancelled = false;

    const ids = generalDesignRows
      .map((row) => getRpGpLaborEffortId(row))
      .filter((id) => Number.isFinite(id));

    const loadOneSection = async (contractSectionsId) => {
      setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "loading" }));
      try {
        let users = generalUsersByContractRef.current[contractSectionsId];
        if (!Array.isArray(users)) {
          const data = await getGeneralDesignUsersWithReports(
            month,
            year,
            contractSectionsId,
          );
          if (cancelled) return;
          users = Array.isArray(data) ? data : [];
          setGeneralUsersByContract((prev) => ({
            ...prev,
            [contractSectionsId]: users,
          }));
        }

        await Promise.all(
          users.map(async (user) => {
            const key = `${contractSectionsId}-${user.id}`;
            if (generalReportsByKeyRef.current[key] !== undefined) return;
            try {
              const fetched = await getGeneralDesignUserReports(
                month,
                year,
                contractSectionsId,
                user.id,
              );
              if (cancelled) return;
              setGeneralReportsByKey((prev) => ({
                ...prev,
                [key]: parseUserReportsPayload(fetched),
              }));
            } catch {
              if (cancelled) return;
              setGeneralReportsByKey((prev) => ({
                ...prev,
                [key]: { correction: {}, reports: [] },
              }));
            }
          }),
        );

        if (cancelled) return;
        setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "ready" }));
      } catch {
        if (cancelled) return;
        setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "error" }));
      }
    };

    const SECTION_CONCURRENCY = 3;
    const run = async () => {
      for (let i = 0; i < ids.length; i += SECTION_CONCURRENCY) {
        if (cancelled) return;
        const chunk = ids.slice(i, i + SECTION_CONCURRENCY);
        await Promise.all(chunk.map(loadOneSection));
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [isAdms, generalDesignRows, month, year]);

  const projectsByUser = useMemo(() => {
    const map = {};
    generalDesignUsers.forEach((user) => {
      map[user.id] = [];
    });
    generalDesignRows.forEach((row) => {
      const csId = getRpGpLaborEffortId(row);
      const users = generalUsersByContract[csId];
      if (!Array.isArray(users)) return;
      users.forEach((user) => {
        if (!map[user.id]) map[user.id] = [];
        map[user.id].push(row);
      });
    });
    return map;
  }, [generalDesignUsers, generalDesignRows, generalUsersByContract]);

  const usersByContractReady = useMemo(
    () =>
      generalDesignRows.length > 0 &&
      generalDesignRows.every((row) =>
        Array.isArray(generalUsersByContract[getRpGpLaborEffortId(row)]),
      ),
    [generalDesignRows, generalUsersByContract],
  );

  useEffect(() => {
    if (!isAdms) return;
    const userIds = uniqueUsersById([
      generalDesignUsers,
      ...Object.values(generalUsersByContract),
    ])
      .map((user) => Number(user.id))
      .filter((id) => Number.isFinite(id));
    const toLoad = userIds.filter((id) => approvalByUserId[id] === undefined);
    if (toLoad.length === 0) return;

    let cancelled = false;
    const load = async () => {
      const loaded = {};
      const BATCH_SIZE = 8;
      for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = toLoad.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async (userId) => {
            try {
              const approval = await getGeneralDesignApproval(
                Number(year),
                Number(month),
                userId,
              );
              return [userId, approval];
            } catch {
              return [userId, null];
            }
          }),
        );
        results.forEach(([userId, approval]) => {
          loaded[userId] = approval || null;
        });
      }
      if (!cancelled) {
        setApprovalByUserId((prev) => ({ ...prev, ...loaded }));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAdms, generalDesignUsers, generalUsersByContract, month, year, approvalByUserId]);

  const approvalUserOptions = useMemo(
    () =>
      generalDesignUsers.map((user) => ({
        value: user.id,
        label: formatUserLabel(user),
      })),
    [generalDesignUsers],
  );

  const openSendApprovalModal = async () => {
    if (isReadOnly) return;
    try {
      const usersSummary = await getUsersSummary({ isActive: true, pageSize: 1000 });
      const approvers = (usersSummary?.users || []).map((u) => ({
        value: u.id,
        label: [u.surname, u.name].filter(Boolean).join(" ").trim() || `ID ${u.id}`,
      }));
      setApproverOptions(approvers);
      setSelectedApprovalUser(null);
      setSelectedApprovers([]);
      setSendApprovalModalOpen(true);
    } catch (err) {
      alert(err?.message || "Не удалось загрузить данные для согласования");
    }
  };

  const submitSendApproval = async () => {
    if (isReadOnly) return;
    if (!selectedApprovalUser?.value) {
      alert("Выберите сотрудника для согласования");
      return;
    }
    const approverIds = selectedApprovers.map((a) => a.value);
    if (approverIds.length === 0) {
      alert("Выберите хотя бы одного согласующего");
      return;
    }
    try {
      setSendingApproval(true);
      const created = await createGeneralDesignApproval(
        Number(selectedApprovalUser.value),
        Number(year),
        Number(month),
        approverIds,
      );
      setApprovalByUserId((prev) => ({
        ...prev,
        [selectedApprovalUser.value]:
          created || prev[selectedApprovalUser.value] || null,
      }));
      alert("Сотрудник Генплан РП успешно отправлен на согласование");
      setSendApprovalModalOpen(false);
    } catch (err) {
      alert(err?.message || "Ошибка отправки на согласование Генплан РП");
    } finally {
      setSendingApproval(false);
    }
  };

  const selectedUserApproval = selectedApprovalUser?.value
    ? approvalByUserId[selectedApprovalUser.value]
    : null;
  const hasExistingApproval = Boolean(selectedUserApproval?.id);
  const approvalCreatorId =
    selectedUserApproval?.creator_id ?? selectedUserApproval?.creator?.id ?? null;
  const canDeleteExistingApproval =
    hasExistingApproval &&
    approvalCreatorId != null &&
    String(approvalCreatorId) === String(userData?.id);

  const deleteExistingApproval = async () => {
    if (isReadOnly || !selectedApprovalUser?.value) return;
    await deleteApprovalForUser({
      id: selectedApprovalUser.value,
      name: selectedApprovalUser.label,
    });
    setSelectedApprovers([]);
  };

  const usersWithoutApprovalCount = useMemo(
    () =>
      generalDesignUsers.filter((user) => !approvalByUserId[user.id]?.id).length,
    [generalDesignUsers, approvalByUserId],
  );

  if (!isAdms) return null;

  const hasListData = isUsersGrouping
    ? generalDesignUsers.length > 0
    : generalDesignRows.length > 0;

  return (
    <>
      <div className="pp-table-wrap">
        <table className="pp-table">
          <thead>
            <tr
              className="pp-user-row"
              onClick={() => setGeneralDesignExpanded((v) => !v)}
            >
              <th>
                <span
                  className={`pp-expand ${generalDesignExpanded ? "expanded" : ""}`}
                  aria-hidden
                >
                  ›
                </span>
                Генплан РП
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {generalDesignExpanded && (
        <>
          <GeneralDesignTable
            grouping={grouping}
            generalDesignLoading={generalDesignLoading}
            generalDesignError={generalDesignError}
            generalDesignRows={generalDesignRows}
            generalDesignUsers={generalDesignUsers}
            projectsByUser={projectsByUser}
            usersByContractReady={usersByContractReady}
            expandedGeneralProjects={expandedGeneralProjects}
            expandedGeneralTopUsers={expandedGeneralTopUsers}
            generalUsersByContract={generalUsersByContract}
            loadingGeneralUsers={loadingGeneralUsers}
            expandedGeneralUsers={expandedGeneralUsers}
            generalReportsByKey={generalReportsByKey}
            loadingGeneralReports={loadingGeneralReports}
            toggleGeneralProject={toggleGeneralProject}
            toggleGeneralTopUser={toggleGeneralTopUser}
            toggleGeneralUser={toggleGeneralUser}
            savingId={savingId}
            setSavingId={setSavingId}
            setConfirmModal={setConfirmModal}
            setCommentModal={setCommentModal}
            updateGeneralDesignCorrection={updateGeneralDesignCorrection}
            setGeneralReportsByKey={setGeneralReportsByKey}
            formatMoney={formatMoney}
            month={month}
            year={year}
            approvalByUserId={approvalByUserId}
            isUserOnApproval={isUserOnApproval}
            canDeleteApprovalForUser={canDeleteApprovalForUser}
            deleteApprovalForUser={deleteApprovalForUser}
            deletingApproval={deletingApproval}
            sectionTotalsState={sectionTotalsState}
            isReadOnly={isReadOnly}
          />

          {!isReadOnly && (
            <div className="pp-actions">
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={openSendApprovalModal}
                disabled={
                  generalDesignLoading ||
                  !hasListData ||
                  usersWithoutApprovalCount === 0
                }
                title={
                  usersWithoutApprovalCount === 0 && hasListData
                    ? "Все сотрудники уже отправлены на согласование"
                    : undefined
                }
              >
                Отправить на согласование
              </button>
            </div>
          )}
        </>
      )}

      {sendApprovalModalOpen && (
        <div
          className="pp-modal-overlay"
          onClick={() => !sendingApproval && setSendApprovalModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-gp-send-approval-title"
        >
          <div
            className="pp-modal pp-send-approval-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div id="pp-gp-send-approval-title" className="pp-modal-title">
              Отправка Генплан РП на согласование
            </div>
            <div className="pp-modal-body">
              <label className="pp-modal-label">Сотрудник (фигурант):</label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={approvalUserOptions}
                value={selectedApprovalUser}
                onChange={setSelectedApprovalUser}
                placeholder="Выберите сотрудника"
                isClearable
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {hasExistingApproval && (
                <div className="pp-existing-approval-note">
                  Для этого сотрудника уже создано согласование за{" "}
                  {MONTH_NAMES[month - 1]} {year} (статус:{" "}
                  {getApprovalStatusText(selectedUserApproval)}).
                  {canDeleteExistingApproval && (
                    <button
                      type="button"
                      className="pp-existing-approval-delete-btn"
                      onClick={deleteExistingApproval}
                      disabled={deletingApproval}
                    >
                      {deletingApproval ? "Удаление..." : "Удалить согласование"}
                    </button>
                  )}
                </div>
              )}

              <label className="pp-modal-label pp-modal-label--mt">
                Кому отправить на согласование:
              </label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={approverOptions}
                value={selectedApprovers}
                onChange={(opts) => setSelectedApprovers(opts || [])}
                placeholder="Выберите согласующих"
                isMulti
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div className="pp-modal-actions">
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={() => setSendApprovalModalOpen(false)}
                disabled={sendingApproval}
              >
                Отмена
              </button>
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={submitSendApproval}
                disabled={sendingApproval || hasExistingApproval}
              >
                {sendingApproval ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GeneralDesignSection;
